import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, errorText, useFetch } from "../api";
import { Alerts, Badge, ConfirmDialog, EmptyRow, Field, Loading, Modal, Page, Pager, Photo, Tabs } from "../components/ui";
import { money, PAGE_SIZE, qs, setQuery, when } from "../format";

const EMPTY = {
  name: "",
  durationDays: "",
  minAmount: "",
  maxAmount: "",
  multiplier: "",
  status: "active",
};

function formFrom(row) {
  if (!row) return EMPTY;
  return {
    name: row.name || "",
    durationDays: row.durationDays ?? "",
    minAmount: row.minAmount ?? "",
    maxAmount: row.maxAmount ?? "",
    multiplier: row.multiplier ?? "",
    status: row.status || "active",
  };
}

function checkImage(file) {
  if (!file) return "";
  if (file.type && !["image/jpeg", "image/png", "image/webp"].includes(file.type)) return "Use a jpg, png or webp image";
  if (file.size > 2 * 1024 * 1024) return "Image must be 2MB or smaller";
  return "";
}

function packageBody(form, initial) {
  const name = form.name.trim();
  if (!initial) {
    const body = {
      durationDays: Number(form.durationDays),
      minAmount: Number(form.minAmount),
      maxAmount: Number(form.maxAmount),
      multiplier: Number(form.multiplier),
      status: form.status || "active",
    };
    if (name) body.name = name;
    return body;
  }

  const body = {};
  if (name !== (initial.name || "")) body.name = name;
  if (Number(form.durationDays) !== Number(initial.durationDays)) body.durationDays = Number(form.durationDays);
  if (Number(form.minAmount) !== Number(initial.minAmount)) body.minAmount = Number(form.minAmount);
  if (Number(form.maxAmount) !== Number(initial.maxAmount)) body.maxAmount = Number(form.maxAmount);
  if (Number(form.multiplier) !== Number(initial.multiplier)) body.multiplier = Number(form.multiplier);
  if (form.status !== (initial.status || "active")) body.status = form.status;
  return body;
}

function asFormData(fields, image) {
  const data = new FormData();
  Object.entries(fields).forEach(([key, value]) => data.append(key, value));
  if (image) data.append("image", image);
  return data;
}

export default function Packages() {
  const [params, setParams] = useSearchParams();
  const page = Math.max(1, Number(params.get("page")) || 1);
  const status = params.get("status") || "";
  const list = useFetch("/packages" + qs({ page, limit: PAGE_SIZE, status }));
  const rows = Array.isArray(list.data?.data) ? list.data.data : [];
  const [editor, setEditor] = useState(null);
  const [notice, setNotice] = useState("");
  const [removing, setRemoving] = useState(null);
  const [removeBusy, setRemoveBusy] = useState(false);
  const [removeError, setRemoveError] = useState("");

  function askDelete(row, event) {
    if (event) event.stopPropagation();
    setRemoveError("");
    setRemoving(row);
  }

  async function removePackage() {
    if (!removing) return;
    setRemoveBusy(true);
    setRemoveError("");
    try {
      await api("/packages/" + removing.id, { method: "DELETE" });
      setRemoving(null);
      setEditor(null);
      setNotice("Package deleted");
      list.reload();
    } catch (err) {
      setRemoveError(errorText(err) || "Package could not be deleted");
    } finally {
      setRemoveBusy(false);
    }
  }

  return (
    <Page
      title="Packages"
      text="Paused and archived packages stay hidden on the website. A member invests between the minimum and maximum. Multiplier 2 means a 100 purchase returns 200. Delete removes the package and its image. Purchases already made keep their amount, multiplier, and daily ROI."
      actions={<button type="button" className="btn" onClick={() => setEditor(EMPTY)}>New package</button>}
    >
      <Alerts error={list.error} notice={notice} />
      <Tabs
        value={status}
        onChange={(value) => setQuery(params, setParams, { status: value, page: "" })}
        options={[
          { value: "", label: "All" },
          { value: "active", label: "Active" },
          { value: "paused", label: "Paused" },
          { value: "archived", label: "Archived" },
        ]}
      />
      {list.loading ? <Loading /> : (
        <div className="table-wrap">
          <table className="wide">
            <thead>
              <tr>
                <th>Image</th>
                <th>Name</th>
                <th>Code</th>
                <th>Days</th>
                <th className="num">Minimum</th>
                <th className="num">Maximum</th>
                <th>Return</th>
                <th>Status</th>
                <th>Created</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {rows.length ? rows.map((row) => (
                <tr key={row.id} className="click" onClick={() => setEditor(row)}>
                  <td><Photo src={row.image} alt="" /></td>
                  <td>{row.name}</td>
                  <td className="code">{row.code}</td>
                  <td>{row.durationDays}</td>
                  <td className="num">{money(row.minAmount)}</td>
                  <td className="num">{money(row.maxAmount)}</td>
                  <td>{row.multiplier}x</td>
                  <td><Badge value={row.status} /></td>
                  <td>{when(row.createdAt)}</td>
                  <td>
                    <button type="button" className="btn danger small" onClick={(event) => askDelete(row, event)}>Delete</button>
                  </td>
                </tr>
              )) : <EmptyRow cols={10} text="No packages" />}
            </tbody>
          </table>
        </div>
      )}
      {list.data ? (
        <Pager page={page} limit={PAGE_SIZE} total={list.data.total} onPage={(next) => setQuery(params, setParams, { page: next <= 1 ? "" : next })} />
      ) : null}
      {editor ? (
        <PackageForm
          key={editor.id || "new"}
          initial={editor.id ? editor : null}
          onClose={() => setEditor(null)}
          onSaved={(message) => {
            setEditor(null);
            setNotice(message);
            list.reload();
          }}
          onDelete={() => askDelete(editor)}
        />
      ) : null}
      {removing ? (
        <ConfirmDialog
          title="Delete package"
          text={"Delete " + (removing.name || "this package") + (removing.code ? " (" + removing.code + ")" : "") + "? It leaves the catalog and its image is removed. Purchases already made keep their amount, multiplier, and daily ROI."}
          error={removeError}
          busy={removeBusy}
          danger
          confirmLabel="Delete package"
          onClose={() => { if (!removeBusy) setRemoving(null); }}
          onConfirm={removePackage}
        />
      ) : null}
    </Page>
  );
}

function PackageForm({ initial, onClose, onSaved, onDelete }) {
  const [form, setForm] = useState(formFrom(initial));
  const [image, setImage] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [errors, setErrors] = useState({});

  function set(key, value) {
    setForm((current) => ({ ...current, [key]: value }));
  }

  async function onSubmit(event) {
    event.preventDefault();
    const imageError = checkImage(image);
    if (imageError) {
      setErrors({ image: imageError });
      return;
    }
    const fields = packageBody(form, initial);
    if (initial && !image && !Object.keys(fields).length) {
      setError("No changes to save");
      return;
    }
    setBusy(true);
    setError("");
    setErrors({});
    const body = image ? asFormData(fields, image) : fields;
    try {
      if (initial) await api("/packages/" + initial.id, { method: "PATCH", body });
      else await api("/packages", { method: "POST", body });
      onSaved(initial ? "Package saved" : "Package created");
    } catch (err) {
      setError(errorText(err));
      setErrors(err.errors || {});
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal title={initial ? "Edit package" : "New package"} wide onClose={onClose}>
      <form onSubmit={onSubmit}>
        <Alerts error={error} />
        <div className="form-grid">
          <Field label="Name" error={errors.name} hint="Optional. A blank name is saved as package.">
            <input maxLength={80} value={form.name} onChange={(event) => set("name", event.target.value)} />
          </Field>
          <Field label="Duration days" error={errors.durationDays}>
            <input type="number" min="1" max="1000" step="1" value={form.durationDays} onChange={(event) => set("durationDays", event.target.value)} />
          </Field>
          <Field label="Minimum amount" error={errors.minAmount} hint="Lowest amount a member can invest">
            <input type="number" min="0" step="0.01" value={form.minAmount} onChange={(event) => set("minAmount", event.target.value)} />
          </Field>
          <Field label="Maximum amount" error={errors.maxAmount} hint="Must be at least the minimum">
            <input type="number" min="0" step="0.01" value={form.maxAmount} onChange={(event) => set("maxAmount", event.target.value)} />
          </Field>
          <Field label="Multiplier" error={errors.multiplier} hint="2 means a 100 purchase returns 200 in total">
            <input type="number" min="0" step="0.01" value={form.multiplier} onChange={(event) => set("multiplier", event.target.value)} />
          </Field>
          <Field label="Status" error={errors.status}>
            <select value={form.status} onChange={(event) => set("status", event.target.value)}>
              <option value="active">Active</option>
              <option value="paused">Paused</option>
              <option value="archived">Archived</option>
            </select>
          </Field>
          <div className="wide">
            <Field label="Image" error={errors.image} hint="Optional. jpg, png or webp, max 2MB. A new file replaces the old one.">
              <input type="file" accept="image/jpeg,image/png,image/webp,.jpg,.jpeg,.png,.webp" onChange={(event) => setImage((event.target.files && event.target.files[0]) || null)} />
            </Field>
            {image ? <p className="hint">{image.name}</p> : null}
            {initial && initial.image && !image ? <Photo src={initial.image} alt="" /> : null}
          </div>
        </div>
        {initial ? <p className="hint">Code {initial.code} is set by the server and is not edited. Updated {when(initial.updatedAt)}.</p> : <p className="hint">The server creates the package code. Do not send one.</p>}
        <div className="form-actions">
          {initial ? (
            <button type="button" className="btn danger start" disabled={busy} onClick={onDelete}>Delete</button>
          ) : null}
          <button type="button" className="btn ghost" onClick={onClose}>Cancel</button>
          <button className="btn" disabled={busy}>{busy ? "Saving..." : "Save package"}</button>
        </div>
      </form>
    </Modal>
  );
}
